import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Microphone, Camera, NotePencil, X, Sparkle, Stop } from '@phosphor-icons/react';

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || '').split(',')[1] || '');
  r.onerror = rej;
  r.readAsDataURL(file);
});

/**
 * Modal that lets the creator attach a "moment" to their bubble.
 * Three kinds in v1: voice (mic recording), photo (file pick), wish (text + tag).
 */
const CargoComposer = ({ bubble, open, onClose, onAttached }) => {
  const [kind, setKind] = useState('wish');
  const [text, setText] = useState('');
  const [moment, setMoment] = useState('');
  const [recording, setRecording] = useState(false);
  const [audioB64, setAudioB64] = useState(null);
  const [photoB64, setPhotoB64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!open) {
      setKind('wish'); setText(''); setMoment('');
      setAudioB64(null); setPhotoB64(null); setRecording(false);
    }
  }, [open]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result || '').split(',')[1] || '');
          r.readAsDataURL(blob);
        });
        setAudioB64(b64);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
      // hard stop at 30s
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); setRecording(false); }, 30_000);
    } catch (err) {
      toast.error('mic permission denied');
    }
  };

  const stopRec = () => {
    if (mediaRecRef.current && mediaRecRef.current.state === 'recording') {
      mediaRecRef.current.stop();
    }
    setRecording(false);
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) { toast.error('photo too large (5mb max)'); return; }
    const b64 = await fileToB64(f);
    setPhotoB64(b64);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = { kind, moment: moment || null };
      if (kind === 'wish') body.text = text;
      if (kind === 'voice') body.blob_b64 = audioB64;
      if (kind === 'photo') {
        body.blob_b64 = photoB64;
        body.text = text || null;
      }
      // Curator gate — ask the Bubble first
      try {
        const { data: pre } = await api.post(`/bubbles/${bubble.id}/cargo/preview`, body);
        if (pre && pre.accepted === false) {
          toast(`${bubble.name} says…`, { description: pre.reason });
          setSubmitting(false);
          return;
        }
      } catch { /* preview is optional */ }
      const { data } = await api.post(`/bubbles/${bubble.id}/cargo`, body);
      toast.success(`${bubble.name} is carrying it.`, { description: data.commentary });
      onAttached?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not attach');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = (() => {
    if (kind === 'wish') return text.trim().length > 0;
    if (kind === 'voice') return !!audioB64;
    if (kind === 'photo') return !!photoB64;
    if (kind === 'clip')  return text.trim().length >= 8;
    return false;
  })();

  const submitClip = async () => {
    setSubmitting(true);
    try {
      toast.loading('Sora is rendering your clip…', { id: 'sora' });
      const { data } = await api.post(`/bubbles/${bubble.id}/cargo/clip`,
        { prompt: text.trim(), moment: moment || null });
      toast.success(`${bubble.name} carries a 12s clip.`,
        { id: 'sora', description: data.commentary });
      onAttached?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'video gen failed', { id: 'sora' });
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cargo-composer"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.96 }}
            className="w-full max-w-md glass rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="cargo-close" onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
              <X size={18} weight="bold" />
            </button>
            <div className="text-center mb-5">
              <div className="text-[10px] uppercase tracking-widest text-bx-cyan">give {bubble.name} something to carry</div>
              <div className="font-display font-bold text-xl mt-1">Cargo</div>
              <div className="text-xs text-white/65 mt-1">
                Your Bubble will hold this and share it with whoever it meets.
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-4">
              {[
                { k: 'wish',  icon: NotePencil, label: 'Wish' },
                { k: 'voice', icon: Microphone, label: 'Voice' },
                { k: 'photo', icon: Camera,     label: 'Photo' },
                { k: 'clip',  icon: Sparkle,    label: 'Clip' },
              ].map((opt) => (
                <button key={opt.k}
                  data-testid={`cargo-kind-${opt.k}`}
                  onClick={() => setKind(opt.k)}
                  className={`rounded-2xl p-3 flex flex-col items-center gap-1 border transition ${
                    kind === opt.k
                      ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan'
                      : 'border-white/10 text-white/60 hover:border-white/30'
                  }`}>
                  <opt.icon size={20} weight="duotone" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">{opt.label}</span>
                </button>
              ))}
            </div>

            {kind === 'wish' && (
              <textarea
                data-testid="cargo-wish-text"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 180))}
                placeholder="say what's true. one line."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-bx-cyan"
              />
            )}

            {kind === 'voice' && (
              <div className="rounded-2xl border border-white/10 p-5 flex flex-col items-center gap-3">
                {!audioB64 && !recording && (
                  <button data-testid="cargo-rec-start" onClick={startRec}
                    className="h-16 w-16 rounded-full bg-bx-magenta/20 ring-2 ring-bx-magenta grid place-items-center hover:bg-bx-magenta/30 transition">
                    <Microphone size={28} weight="fill" className="text-bx-magenta" />
                  </button>
                )}
                {recording && (
                  <button data-testid="cargo-rec-stop" onClick={stopRec}
                    className="h-16 w-16 rounded-full bg-red-500/30 ring-2 ring-red-400 grid place-items-center animate-pulse">
                    <Stop size={26} weight="fill" className="text-red-300" />
                  </button>
                )}
                {audioB64 && !recording && (
                  <>
                    <audio data-testid="cargo-audio-preview" controls
                      src={`data:audio/webm;base64,${audioB64}`} className="w-full" />
                    <button onClick={() => setAudioB64(null)} className="text-xs text-white/65 hover:text-white">re-record</button>
                  </>
                )}
                <div className="text-[10px] uppercase tracking-widest text-white/60">
                  {recording ? 'recording · max 30s' : audioB64 ? 'ready' : 'tap to record'}
                </div>
              </div>
            )}

            {kind === 'photo' && (
              <div className="rounded-2xl border border-white/10 p-5 flex flex-col items-center gap-3">
                {!photoB64 ? (
                  <label className="cursor-pointer" data-testid="cargo-photo-pick">
                    <div className="h-16 w-16 rounded-full bg-bx-cyan/20 ring-2 ring-bx-cyan grid place-items-center hover:bg-bx-cyan/30 transition">
                      <Camera size={28} weight="fill" className="text-bx-cyan" />
                    </div>
                    <input type="file" accept="image/*" hidden onChange={onPhoto} />
                  </label>
                ) : (
                  <>
                    <img alt="" src={`data:image/png;base64,${photoB64}`}
                      className="h-32 w-32 rounded-2xl object-cover" />
                    <button onClick={() => setPhotoB64(null)} className="text-xs text-white/65 hover:text-white">replace</button>
                  </>
                )}
                <input type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, 120))}
                  placeholder="caption (optional)"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-cyan" />
              </div>
            )}

            {kind === 'clip' && (
              <div className="rounded-2xl border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={14} weight="duotone" className="text-bx-volt" />
                  <span className="text-[10px] uppercase tracking-widest text-bx-volt">sora-2 · 12s clip</span>
                </div>
                <textarea
                  data-testid="cargo-clip-prompt"
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, 240))}
                  placeholder="describe the moment your bubble carries — a sunrise on a rooftop, fireworks over the bay, a quiet kitchen at 3am"
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-volt" />
                <div className="text-[10px] text-white/55 mt-1">
                  Takes ~20s to generate. Uses one cargo slot. No upload needed.
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">what's this moment?</div>
              <div className="flex flex-wrap gap-1.5">
                {['win', 'loss', 'birthday', 'milestone', 'kindness', 'wish', 'secret'].map((m) => (
                  <button key={m}
                    data-testid={`cargo-moment-${m}`}
                    onClick={() => setMoment(moment === m ? '' : m)}
                    className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition ${
                      moment === m ? 'bg-bx-volt text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="cargo-submit"
              disabled={!canSubmit || submitting}
              onClick={kind === 'clip' ? submitClip : submit}
              className={`mt-5 w-full py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition ${
                canSubmit && !submitting ? 'btn-volt' : 'bg-white/5 text-white/55 cursor-not-allowed'
              }`}>
              <Sparkle size={16} weight="fill" />
              {submitting ? (kind === 'clip' ? 'rendering…' : 'attaching…') : `Have ${bubble.name} carry it`}
            </button>
            <div className="text-[10px] text-white/55 text-center mt-2">
              Bubbles can only carry one new moment every 72 hours. Make it count.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CargoComposer;
